import { cn } from '@/lib/cn';
import type { Theme } from '@personalizehq/schemas/ir';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import type { LucideIcon } from 'lucide-react';
import { Children, isValidElement, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';

// The control vocabulary (B0). Everything here is native or ~20 lines; the one dependency is
// `@radix-ui/react-popover`, and only because a panel inside an `overflow-y-auto` column clips an
// absolutely-positioned menu, which is exactly the problem a portal exists to solve.
//
// THE LAW THIS FILE OBEYS. `inspector.test.tsx` walks every control BY ARIA ROLE and asserts the
// document still publishes. So a widget here is not free to be a div with a pointer handler:
//
//   toggle group  → <button role="radio"> in a role="radiogroup"
//   slider        → native <input type="range">   (role="slider", min/max for free)
//   number        → native <input type="number">  (role="spinbutton") — the SCRUB IS ON THE LABEL,
//                   which is why the input underneath survives untouched
//   popover       → a real <button> trigger, marked [data-popover] so the walker can open it
//   disclosure    → a real <button>, marked [data-more]
//
// A control the test cannot reach by role is a control a screen-reader user cannot reach either.
// That is not a tax on the design; it is the same rule as A4.

const CONTROL =
  'h-7 min-w-0 rounded-md border bg-transparent px-1.5 text-[12px] outline-none focus:border-indigo-500';

// ---------------------------------------------------------------------------- section

/** Collapsible, and native — `<details>` needs no state, no dep, and no aria wiring.
 *  `hasIssue` FORCES it open: there is zero section→field mapping to drift, and an error can never
 *  hide behind a closed header. */
export function Section({
  title,
  hasIssue,
  defaultOpen = true,
  children,
}: {
  title: string;
  hasIssue?: boolean;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <details open={hasIssue || defaultOpen} className="group border-b">
      <summary className="flex cursor-default select-none items-center gap-1 px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground marker:content-['']">
        <span className="transition-transform group-open:rotate-90">›</span>
        {title}
        {hasIssue ? <span className="ml-auto size-1.5 rounded-full bg-destructive" /> : null}
      </summary>
      <div className="space-y-2 px-3 pb-3">{children}</div>
    </details>
  );
}

// ---------------------------------------------------------------------------- disclosure

/** PROGRESSIVE DISCLOSURE — the actual answer to "a stack shows ~25 rows".
 *
 *  A row whose value is not set does not appear at all; it waits behind a pill. `max_width` and
 *  `min_width` simply do not exist on screen until you have used them. This is what takes a `stack`
 *  from ~25 rows to about 8, and it is the single idea that makes the panel authorable.
 *
 *  It reads `set` off its children rather than taking two lists, so the call site stays a flat run
 *  of rows and no row can drift into the wrong bucket. */
export function Disclosure({ children }: { children: React.ReactNode }): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  const rows = Children.toArray(children).filter(isValidElement) as ReactElement<{ set?: boolean }>[];
  const shown = rows.filter((row) => row.props.set);
  const hidden = rows.filter((row) => !row.props.set);

  if (!rows.length) return null;
  return (
    <>
      {shown}
      {open ? hidden : null}
      {hidden.length ? (
        <button
          type="button"
          data-more=""
          aria-label={open ? 'fewer' : `${hidden.length} more`}
          className="text-[11px] text-muted-foreground hover:text-foreground"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? '− fewer' : `+ ${hidden.length} more`}
        </button>
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------- toggle group

/** An enum as icons. `role="radio"` in a `role="radiogroup"` — the walker clicks each one, and a
 *  screen reader announces it as the choice it is. */
export function ToggleGroup<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T | undefined;
  options: readonly { value: T; icon: LucideIcon; title?: string }[];
  onChange: (value: T) => void;
}): React.JSX.Element {
  return (
    <div role="radiogroup" aria-label={label} className="flex gap-0.5 rounded-md border p-0.5">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={value === option.value}
          aria-label={`${label} ${option.value}`}
          title={option.title ?? option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            'flex size-6 items-center justify-center rounded text-muted-foreground',
            value === option.value && 'bg-indigo-500/20 text-indigo-300'
          )}
        >
          <option.icon className="size-3.5" />
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------- slider

/** For BOUNDED values only — `opacity` (0–1) and `parallax` (−1…1). A native range hands the test
 *  `role="slider"` with `min`/`max`/`step` for free, so the widget carries the schema's bounds and
 *  cannot author a value outside them. (Unbounded extents get `useScrub` instead: a slider needs
 *  two ends, and `gap` has only one.) */
export function Slider({
  label,
  value,
  fallback,
  min,
  max,
  step,
  onChange,
}: {
  label: string;
  value: number | undefined;
  /** What the range sits at while the key is ABSENT — the schema's own default, never 0. */
  fallback: number;
  min: number;
  max: number;
  step: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  return (
    <>
      <input
        type="range"
        aria-label={label}
        min={min}
        max={max}
        step={step}
        value={value ?? fallback}
        onChange={(e) => onChange(Number(e.target.value))}
        className="h-7 min-w-0 flex-1 accent-indigo-500"
      />
      <input
        type="number"
        aria-label={`${label} value`}
        min={min}
        max={max}
        step={step}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? fallback : Number(e.target.value))}
        className={cn(CONTROL, 'w-[56px]')}
      />
    </>
  );
}

// ---------------------------------------------------------------------------- scrub

export type ScrubProps = { onPointerDown: (e: React.PointerEvent) => void };

/** Drag the LABEL to change the number. Plasmic's `DraggableDimLabel`, and the reason it is on the
 *  label and not the input: the `<input type="number">` underneath stays a plain spinbutton, which
 *  is precisely what keeps `inspector.test.tsx` able to walk it. Shared by every unbounded extent
 *  in the panel (gap, padding, spacing), which is what makes the whole thing feel alive. */
export function useScrub(opts: {
  value: number | undefined;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
}): ScrubProps {
  // The drag reads the LATEST value/handler, but the handler itself must be stable — a scrub that
  // re-subscribed mid-drag would drop the pointer. Written in an effect, not during render.
  const latest = useRef(opts);
  useEffect(() => {
    latest.current = opts;
  });

  return useMemo(
    () => ({
      onPointerDown: (e: React.PointerEvent) => {
        const { value, min = 0, max = Number.MAX_SAFE_INTEGER, step = 1 } = latest.current;
        let acc = value ?? 0;
        let last = e.clientX;
        const target = e.currentTarget as HTMLElement;

        const move = (ev: PointerEvent): void => {
          // `movementX` is what pointer lock reports; the clientX delta is the fallback for when
          // the lock is refused (and for jsdom, which has neither).
          const dx = ev.movementX || ev.clientX - last;
          last = ev.clientX;
          acc = Math.min(max, Math.max(min, acc + dx * step));
          latest.current.onChange(Number((Math.round(acc / step) * step).toFixed(4)));
        };
        const up = (): void => {
          window.removeEventListener('pointermove', move);
          window.removeEventListener('pointerup', up);
          document.exitPointerLock?.();
        };

        target.requestPointerLock?.();
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
      },
    }),
    []
  );
}

// ---------------------------------------------------------------------------- popover

/** The trigger is a real `<button>` carrying `[data-popover]`: the walker opens every one of them
 *  before it walks, so nothing hides inside. */
export function Popover({
  label,
  trigger,
  children,
}: {
  label: string;
  trigger: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <PopoverPrimitive.Root>
      <PopoverPrimitive.Trigger
        data-popover=""
        aria-label={label}
        className={cn(CONTROL, 'flex items-center gap-1.5 px-1.5')}
      >
        {trigger}
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          sideOffset={6}
          align="end"
          className="z-50 w-[212px] rounded-md border bg-popover p-2 text-popover-foreground shadow-md"
        >
          {children}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

// ---------------------------------------------------------------------------- token grid

/** A colour swatch shows BOTH halves of the pair, because that is what the token actually is. */
export function Swatch({ pair, className }: { pair?: { light: string; dark: string } | undefined; className?: string }): React.JSX.Element {
  if (!pair) return <span className={cn('size-4 rounded-sm border border-dashed', className)} />;
  return (
    <span className={cn('flex size-4 overflow-hidden rounded-sm border', className)}>
      <span className="w-1/2" style={{ background: pair.light }} />
      <span className="w-1/2" style={{ background: pair.dark }} />
    </span>
  );
}

/** NO RAW COLOUR PICKER, EVER. Every colour on the wire is a token with a light/dark pair (SPEC §4),
 *  so a hex field would silently kill dark mode on every install. Plasmic wraps Pickr because it
 *  permits raw colours; we do not, and the constraint makes the control simpler AND teaches the law.
 *
 *  The swatches are `role="radio"` — a grid of buttons is what this is, and saying so is free. */
export function TokenGrid({
  label,
  kind,
  theme,
  value,
  onChange,
  optional,
}: {
  label: string;
  kind: 'colors' | 'fonts' | 'buttons';
  theme: Theme;
  value: string | null | undefined;
  onChange: (value: string | undefined) => void;
  /** May the token be ABSENT? (`bg` may; a `text`'s `color` may not.) */
  optional?: boolean;
}): React.JSX.Element {
  const keys = Object.keys(theme[kind]);
  const pair = (key: string): { light: string; dark: string } | undefined =>
    kind === 'colors' ? theme.colors[key] : undefined;

  return (
    <Popover
      label={label}
      trigger={
        <>
          {kind === 'colors' ? <Swatch pair={value ? pair(value) : undefined} /> : null}
          <span className="truncate text-[12px]">{value ?? 'none'}</span>
        </>
      }
    >
      <div role="radiogroup" aria-label={label} className="grid grid-cols-6 gap-1">
        {optional ? (
          <button
            type="button"
            role="radio"
            aria-checked={value === undefined || value === null}
            aria-label={`${label} none`}
            title="none"
            onClick={() => onChange(undefined)}
            className="flex size-7 items-center justify-center rounded border border-dashed text-[10px] text-muted-foreground"
          >
            ⃠
          </button>
        ) : null}
        {keys.map((key) => (
          <button
            key={key}
            type="button"
            role="radio"
            aria-checked={value === key}
            aria-label={`${label} ${key}`}
            title={key}
            onClick={() => onChange(key)}
            className={cn(
              'flex size-7 items-center justify-center rounded border',
              value === key && 'ring-2 ring-indigo-500'
            )}
          >
            {kind === 'colors' ? (
              <Swatch pair={pair(key)} className="size-5 border-0" />
            ) : (
              <span className="truncate px-0.5 text-[9px]">{key.slice(0, 3)}</span>
            )}
          </button>
        ))}
      </div>
    </Popover>
  );
}

// ---------------------------------------------------------------------------- angle dial

/** Paired with a number input, never alone: a dial is fast to aim and hopeless to be exact with,
 *  and a gradient angle is a value people copy between documents. */
export function AngleDial({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}): React.JSX.Element {
  const radians = ((value - 90) * Math.PI) / 180;
  const aim = (e: React.PointerEvent<SVGSVGElement>): void => {
    const box = e.currentTarget.getBoundingClientRect();
    const dx = e.clientX - (box.left + box.width / 2);
    const dy = e.clientY - (box.top + box.height / 2);
    const deg = Math.round((Math.atan2(dy, dx) * 180) / Math.PI + 90 + 360) % 360;
    onChange(deg);
  };

  return (
    <>
      <svg
        viewBox="0 0 28 28"
        className="size-7 shrink-0 cursor-grab touch-none"
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          aim(e);
        }}
        onPointerMove={(e) => {
          if (e.currentTarget.hasPointerCapture(e.pointerId)) aim(e);
        }}
      >
        <circle cx="14" cy="14" r="12" className="fill-transparent stroke-border" />
        <line
          x1="14"
          y1="14"
          x2={14 + Math.cos(radians) * 10}
          y2={14 + Math.sin(radians) * 10}
          className="stroke-indigo-400"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
      <input
        type="number"
        aria-label={label}
        min={0}
        max={360}
        value={value}
        onChange={(e) => onChange(Math.min(360, Math.max(0, Number(e.target.value) || 0)))}
        className={cn(CONTROL, 'w-[56px]')}
      />
    </>
  );
}
